import "./array-panel.scss";

import classNames from "classnames";
import * as _ from "lodash";
import type { ReactNode } from "react";
import { useCallback } from "react";

import { Number } from "@/utils";

import Panel from "../base/Panel";
import RedoButton from "../redo/Redo";
import UndoButton from "../undo/Undo";

interface ArrayConfiguration {
    /**
     * The number of lights in the array.
     */
    lights: string;
    /**
     * The direction of the array.
     */
    direction: "right" | "down" | "left" | "up";
    /**
     * The spacing between lights in feet.
     */
    spacing: string;
}

interface ArrayPanelProps {
    /**
     * The current array configuration state.
     */
    arrayConfiguration: ArrayConfiguration;
    /**
     * The undo action handler.
     */
    undo?: () => void;
    /**
     * The redo action handler.
     */
    redo?: () => void;
    /**
     * Callback to update array configuration.
     */
    onConfigureArray: (config: ArrayConfiguration) => void;
    /**
     * Callback to clear the array configuration.
     */
    onClearConfiguration: () => void;
    /**
     * Callback to set which lights are selected.
     */
    setSelectLights: (lights: unknown[], mode: string) => void;
    /**
     * The currently selected lights.
     */
    selectedLights: unknown[];
    /**
     * Callback to deselect all lights.
     */
    deselectAllLights: () => void;
}

/**
 * Panel for configuring array creation with direction, spacing, light count, and undo/redo.
 *
 * @param {ArrayPanelProps} props - The component props.
 * @returns {ReactNode} The rendered array panel.
 */
const ArrayPanel = ({
    arrayConfiguration,
    undo,
    redo,
    onConfigureArray,
    onClearConfiguration,
    setSelectLights,
    selectedLights,
    deselectAllLights,
}: ArrayPanelProps): ReactNode => {
    const onChangeLights = useCallback(
        (value, propName) => {
            onConfigureArray({
                ...arrayConfiguration,
                [propName]: value,
            });
        },
        [arrayConfiguration, onConfigureArray],
    );

    const directions = [
        { value: "right", label: "Right" },
        { value: "down", label: "Down" },
        { value: "left", label: "Left" },
        { value: "up", label: "Up" },
    ];
    const isSingleLight =
        !arrayConfiguration.lights ||
        parseInt(arrayConfiguration.lights, 10) <= 1;

    return (
        <Panel>
            <div className="array-panel">
                <div className="field">
                    <label>Lights:</label>
                    <input
                        style={{ width: 54 }}
                        value={arrayConfiguration.lights}
                        onChange={({ target }) =>
                            onChangeLights(
                                Number.convert(
                                    target ? target.value : "",
                                    arrayConfiguration.lights,
                                    true,
                                ),
                                "lights",
                            )
                        }
                        onBlur={() => {
                            if (!arrayConfiguration.lights) {
                                onClearConfiguration();
                            }
                        }}
                    />
                </div>
                <div
                    className={classNames("field")}
                    style={
                        isSingleLight
                            ? {
                                  opacity: 0,
                                  pointerEvents: "none",
                              }
                            : null
                    }
                >
                    <label>Direction:</label>
                    <div className="select">
                        <select
                            style={{ width: 98 }}
                            value={arrayConfiguration.direction}
                            onChange={({ target }) =>
                                onChangeLights(target.value, "direction")
                            }
                        >
                            {directions.map((option) => (
                                <option
                                    key={`${_.kebabCase(option.label)}-option`}
                                    value={option.value}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div
                    className={classNames("field")}
                    style={
                        isSingleLight
                            ? {
                                  opacity: 0,
                                  pointerEvents: "none",
                              }
                            : null
                    }
                >
                    <label>Spacing:</label>
                    <input
                        style={{ width: 60 }}
                        value={arrayConfiguration.spacing}
                        onChange={({ target }) =>
                            onChangeLights(
                                Number.convert(
                                    target ? target.value : "",
                                    arrayConfiguration.spacing,
                                    false,
                                    4,
                                    true,
                                ),
                                "spacing",
                            )
                        }
                    />
                    <label>ft</label>
                </div>
                <div className="field">
                    <div
                        onClick={() => {
                            setSelectLights([], "multiple");
                            deselectAllLights();
                        }}
                        className={classNames("set-array-button", {
                            disabled: selectedLights.length <= 0,
                        })}
                    >
                        Set Array
                    </div>
                </div>
                <div className="field">
                    <div
                        className={classNames(
                            { disabled: !undo },
                            "undo-button",
                        )}
                        onClick={undo}
                    >
                        <UndoButton color={!undo ? "#ccc" : "#fff"} />
                        <label>Undo</label>
                    </div>
                    <div
                        className={classNames(
                            { disabled: !redo },
                            "redo-button",
                        )}
                        onClick={redo}
                    >
                        <RedoButton color={!redo ? "#ccc" : "#fff"} />
                        <label>Redo</label>
                    </div>
                </div>
            </div>
        </Panel>
    );
};

export default ArrayPanel;
